import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { card, VerifiedBadge, SectionTitle, WebEntryLayout } from './entryHelpers';
import { patientAuthFlow, isDemoAuthEnabled } from '../../auth/patientAuthFlow';

export default function ApprovalPendingScreen() {
  const navigate = useNavigate();
  const state = patientAuthFlow.getState();

  const handleDevApprove = () => {
    patientAuthFlow.devSetApprovalStatus('approved');
    alert('Dev: approvalStatus set to "approved". Go back to Login and sign in again.');
  };

  return (
    <WebEntryLayout>
    <div style={{ display: 'flex', flexDirection: 'column', fontFamily: T.font, flex: 1 }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', background: T.white, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>NeoHealthCard ID Status</div>
        <div style={{ fontSize: 9, color: T.textSec }}>Approval Pending</div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Status icon */}
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `${T.warning}12`, display: 'grid', placeItems: 'center', margin: '0 auto 12px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.warning} strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginBottom: 4 }}>Your NeoHealthCard ID is not approved yet</div>
          {state.nhcId && (
            <div style={{ fontSize: 22, fontWeight: 800, color: T.navy, fontFamily: T.mono, letterSpacing: '0.06em', marginBottom: 4 }}>{state.nhcId}</div>
          )}
          {state.neoVerifyId && (
            <div style={{ fontSize: 10, color: T.textSec, fontFamily: T.mono }}>NeoVerify ID: {state.neoVerifyId}</div>
          )}
          <div style={{ display: 'inline-block', marginTop: 8 }}>
            <VerifiedBadge text="PENDING VERIFICATION" color={T.warning} />
          </div>
        </div>

        {/* Verification status card */}
        <div style={card()}>
          <SectionTitle text="Verification Status" />
          {[
            { label: 'Patient Identity', status: 'PENDING', color: T.warning, note: 'Awaiting verification by NeoHealthCard' },
            { label: 'Registration Data', status: 'SUBMITTED', color: T.teal, note: 'Your registration has been submitted' },
            { label: 'NeoHealthCard ID Approval', status: 'PENDING', color: T.warning, note: 'Approval required before dashboard access' },
          ].map((v, i) => (
            <div key={i} style={{ padding: '8px 0', borderTop: i > 0 ? `1px solid ${T.borderLight || T.border}` : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: T.text }}>{v.label}</span>
                <VerifiedBadge text={v.status} color={v.color} />
              </div>
              <div style={{ fontSize: 8, color: T.textTer, marginTop: 2 }}>{v.note}</div>
            </div>
          ))}
        </div>

        {/* What happens next */}
        <div style={{ padding: '10px 14px', background: `${T.navy}04`, borderRadius: 10, border: `1px solid ${T.navy}10` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.text, marginBottom: 4 }}>What happens next?</div>
          <div style={{ fontSize: 9, color: T.textSec, lineHeight: 1.7 }}>
            Your registration is being reviewed. Once approved, you can login and access the Patient Dashboard. You will be notified when your NeoHealthCard ID is approved.
          </div>
        </div>

        {/* Return to Login */}
        <button
          onClick={() => {
            patientAuthFlow.resetToLogin();
            navigate('/patient/entry', { replace: true });
          }}
          style={{
            width: '100%',
            padding: '13px 0',
            borderRadius: 14,
            border: 'none',
            background: `linear-gradient(135deg, ${T.teal}, ${T.overlap})`,
            color: '#FFF',
            fontSize: 13,
            fontWeight: 700,
            fontFamily: T.font,
            cursor: 'pointer',
            textAlign: 'center',
            boxShadow: `0 4px 14px ${T.teal}40`,
          }}
        >
          Return to Login
        </button>

        {/* Contact support placeholder */}
        <div style={{ textAlign: 'center' }}>
          <span style={{ fontSize: 10, color: T.teal, fontWeight: 600, cursor: 'pointer' }}>Contact Support</span>
        </div>

        {/* Dev-only controls — only rendered when VITE_ENABLE_DEMO_AUTH=true,
            never in a deployed/staged environment. */}
        {isDemoAuthEnabled() && (
          <div style={{ marginTop: 10, padding: '10px 14px', background: '#FEF3C7', borderRadius: 10, border: '1px solid #FCD34D' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>DEV / LOCAL DEMO ONLY</div>
            <button
              onClick={handleDevApprove}
              style={{
                width: '100%',
                padding: '8px 0',
                borderRadius: 8,
                border: '1px solid #92400E',
                background: '#FFFBEB',
                color: '#92400E',
                fontSize: 10,
                fontWeight: 600,
                fontFamily: T.font,
                cursor: 'pointer',
              }}
            >
              Dev: Approve this patient locally
            </button>
            <div style={{ fontSize: 8, color: '#92400E', marginTop: 4, lineHeight: 1.5 }}>
              Sets approvalStatus to "approved" in localStorage. You must then return to Login and sign in again. This does NOT auto-open the dashboard.
            </div>
          </div>
        )}
      </div>
    </div>
    </WebEntryLayout>
  );
}
